import { Link, useLocation } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Context Theme
import { ThemeContext } from "../context/ThemeContext";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const { theme, toggleTheme } = useContext(ThemeContext);

  // Scroll effect
  useEffect(() => {
    if (location.pathname !== "/") {
      setIsScrolled(true);
      return;
    }

    const handleScroll = () => {
      if (window.scrollY > 50) setIsScrolled(true);
      else setIsScrolled(false);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location]);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 w-full flex items-center justify-between px-4 sm:px-8 py-3 z-50 transition-all duration-700 ${
        isScrolled
        ? "bg-blue-800 shadow-md"
        : "bg-transparent"
      }`}
    >
      {/* Logo */}
      <Link to="/" className="flex items-center gap-4 ml-24">
        <img
          src="/logo_igm_only.png"
          alt="Logo IGM"
          className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
        />
      </Link>

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center space-x-2 text-white font-medium mr-24">
        {[
          { name: "Home", to: "/" },
          { name: "About", to: "/about" },
          { name: "Services", to: "/service" },
          { name: "Team", to: "/team" },
          { name: "Clients", to: "/client" },
          { name: "Contact", to: "/contact" },
        ].map((item) => (
          <Link
            key={item.name}
            to={item.to}
            className="px-3 py-2 rounded-lg hover:bg-white hover:text-blue-600 transition duration-300"
          >
            {item.name}
          </Link>
        ))}

        {/* Theme Toggle (Desktop) */}
        <button
          onClick={toggleTheme}
          className="ml-4 p-2 rounded-lg bg-white/20 hover:bg-white/30 transition duration-300"
        >
          {theme === "light" ? (
            // ☀️ Sun Icon (Outline Modern)
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.8"
              stroke="white"
              className="w-6 h-6"
            >
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
          ) : (
            // 🌙 Moon Icon (Outline Modern)
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.8"
              stroke="white"
              className="w-6 h-6"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile: Theme Toggle + Hamburger */}
      <div className="flex items-center gap-3 md:hidden">
        {/* Theme Toggle (Mobile) */}
        <button onClick={toggleTheme} className="text-white">
          {theme === "light" ? (
            // Sun Icon
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.8"
              stroke="white"
              className="w-6 h-6"
            >
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
          ) : (
            // Moon Icon
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.8"
              stroke="white"
              className="w-6 h-6"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-white focus:outline-none"
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 8h16M4 16h16"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="absolute top-full left-0 w-full bg-blue-500 shadow-md md:hidden flex flex-col text-white text-center py-4 space-y-3"
          >
            {[
              { name: "Home", to: "/" },
              { name: "About", to: "/about" },
              { name: "Services", to: "/service" },
              { name: "Team", to: "/team" },
              { name: "Clients", to: "/client" },
              { name: "Contact", to: "/contact" },
            ].map((item) => (
              <Link
                key={item.name}
                to={item.to}
                className="py-2 text-lg hover:bg-white hover:text-blue-600 rounded-md transition duration-200"
              >
                {item.name}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
